import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import type { RootState } from '../index';
import { getProductMasterStats } from '../../services/productMasterService';
import type { CpmStatsResponse } from '../../types/centralProductMaster.types';

interface CpmOverviewState {
    stats: CpmStatsResponse | null;
    loading: 'idle' | 'loading' | 'success' | 'error';
    error: string | null;
    /** Wall-clock ms when stats were last successfully fetched. Drives the hero “Data as of” label. */
    lastFetchedAt: number | null;
    /** Soft-refresh failed while prior stats are still shown. */
    refreshFailed: boolean;
    attentionExpanded: boolean;
    tenantInfoOpen: boolean;
    exciseInfoOpen: boolean;
    confirmingJobId: string | null;
    retriedJobIds: string[];
}

const initialState: CpmOverviewState = {
    stats: null,
    loading: 'idle',
    error: null,
    lastFetchedAt: null,
    refreshFailed: false,
    attentionExpanded: false,
    tenantInfoOpen: false,
    exciseInfoOpen: false,
    confirmingJobId: null,
    retriedJobIds: [],
};

export const fetchOverviewStats = createAsyncThunk(
    'cpmOverview/fetchStats',
    async (_, { rejectWithValue }) => {
        try {
            return await getProductMasterStats();
        } catch (err: unknown) {
            const message =
                err && typeof err === 'object' && 'message' in err
                    ? String((err as { message: string }).message)
                    : 'Failed to load overview stats';
            return rejectWithValue(message);
        }
    }
);

const cpmOverviewSlice = createSlice({
    name: 'cpmOverview',
    initialState,
    reducers: {
        toggleAttentionExpanded(state) {
            state.attentionExpanded = !state.attentionExpanded;
        },
        toggleTenantInfo(state) {
            state.tenantInfoOpen = !state.tenantInfoOpen;
        },
        toggleExciseInfo(state) {
            state.exciseInfoOpen = !state.exciseInfoOpen;
        },
        setConfirmingJobId(state, action: PayloadAction<string | null>) {
            state.confirmingJobId = action.payload;
        },
        markJobRetried(state, action: PayloadAction<string>) {
            if (!state.retriedJobIds.includes(action.payload)) {
                state.retriedJobIds.push(action.payload);
            }
            state.confirmingJobId = null;
        },
        clearRefreshFailed(state) {
            state.refreshFailed = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOverviewStats.pending, (state) => {
                // Soft refresh: keep prior stats visible while refetching.
                state.loading = 'loading';
                state.error = null;
                state.refreshFailed = false;
            })
            .addCase(fetchOverviewStats.fulfilled, (state, action) => {
                state.loading = 'success';
                state.stats = action.payload;
                state.error = null;
                state.lastFetchedAt = Date.now();
                state.refreshFailed = false;
            })
            .addCase(fetchOverviewStats.rejected, (state, action) => {
                // Soft refresh failure: keep prior stats if we have them.
                if (state.stats) {
                    state.loading = 'success';
                    state.refreshFailed = true;
                } else {
                    state.loading = 'error';
                    state.refreshFailed = false;
                }
                state.error = (action.payload as string) || 'Failed to load overview stats';
            });
    },
});

export const {
    toggleAttentionExpanded,
    toggleTenantInfo,
    toggleExciseInfo,
    setConfirmingJobId,
    markJobRetried,
    clearRefreshFailed,
} = cpmOverviewSlice.actions;

export const useCpmOverview = () => useSelector((s: RootState) => s.cpmOverview);

export default cpmOverviewSlice.reducer;
